import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner
} from 'reactstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import {
    getActividadesEconomicasList,
    postActividadesEconomicas,
    deleteActividadesEconomicas,
    getActividadesEconomicasFilter,
    patchActividadesEconomicasUpdate
} from '../../../helpers/backend';
import Pagination from '../../../components/Common/Pagination_ansync';

const ActividadesEconomicas = () => {
    const [params, setParams] = useState({
        count: 0,
        next: null,
        previous: null,
        results: [],
    });
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: ''
    });

    // Efecto inicial para cargar datos
    useEffect(() => {
        fetchData();
    }, []);

    // Efecto para manejar la búsqueda con debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm) {
                getFilterData(`?search=${searchTerm}`);
            } else {
                fetchData();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {
                search: searchTerm,
                limit: 30
            };
            // Si searchTerm está vacío, no enviamos search param o el backend puede retornarlos todos
            // Asumiremos que el backend maneja search vacío o null correctamente, o simplemente llamamos sin params si search es vacío.
            // Pero getActividadesEconomicasList no recibe params en la definición actual del helper (salvo que lo haya cambiado o que axios lo maneje).
            // Revisando helper: export const getActividadesEconomicasList = () => get(`/casos/actividades-economicas/`);
            // No acepta params. Deberia usar getActividadesEconomicasFilter para search o modificar List.
            // Usaré getFilterData para consistencia si hay busqueda, o llamaré a list si no.

            let response;
            if (searchTerm) {
                response = await getActividadesEconomicasFilter(`?search=${searchTerm}&limit=30`);
            } else {
                // Si la lista base tiene paginacion por defecto, usamos esa.
                // El helper getActividadesEconomicasList llama a la url base.
                // El usuario dijo: GET/api/casos/actividades-economicas/?limit=30&offset=30 en el next.
                response = await getActividadesEconomicasList();
            }

            setParams({
                count: response.data.count,
                next: response.data.next,
                previous: response.data.previous,
                results: response.data.results || [],
            });
        } catch (error) {
            console.error(error);
            showAlert('Error al cargar las actividades económicas', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const showAlert = (message, type) => {
        setAlert({ show: true, message, type });
        setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
    };

    const toggleModal = () => {
        setModal(!modal);
        if (!modal) {
            setEditingItem(null);
            setFormData({
                nombre: '',
                descripcion: ''
            });
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            nombre: item.nombre,
            descripcion: item.descripcion || ''
        });
        setModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToSend = {
                nombre: formData.nombre,
                descripcion: formData.descripcion
            };

            if (editingItem) {
                await patchActividadesEconomicasUpdate(editingItem.id, dataToSend);
                showAlert('Actividad actualizada exitosamente', 'success');
            } else {
                await postActividadesEconomicas(dataToSend);
                showAlert('Actividad creada exitosamente', 'success');
            }
            toggleModal();
            fetchData();
        } catch (error) {
            console.error(error);
            showAlert('Error al guardar la actividad', 'danger');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta actividad?')) {
            try {
                await deleteActividadesEconomicas(id);
                showAlert('Actividad eliminada exitosamente', 'success');
                fetchData();
            } catch (error) {
                console.error(error);
                showAlert('Error al eliminar la actividad', 'danger');
            }
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const getFilterData = async (url) => {
        setLoading(true);

        try {
            let finalUrl = '';

            if (url && url.startsWith('http')) {
                const urlObj = new URL(url);
                finalUrl = urlObj.search;
            } else {
                if (url && !url.startsWith('?') && url.length > 0) {
                    finalUrl = '?' + url;
                } else {
                    finalUrl = url || '';
                }

                const urlParams = new URLSearchParams(finalUrl.startsWith('?') ? finalUrl.slice(1) : finalUrl);

                if (searchTerm && !urlParams.has('search')) {
                    urlParams.set('search', searchTerm);
                }

                finalUrl = urlParams.toString() ? `?${urlParams.toString()}` : '';
            }

            const response = await getActividadesEconomicasFilter(finalUrl);
            setParams({
                count: response.data.count,
                next: response.data.next,
                previous: response.data.previous,
                results: response.data.results || [],
            });
        } catch (error) {
            console.error('Error al cargar actividades:', error);
            showAlert('Error al cargar las actividades', 'danger');
        }

        setLoading(false);
    };

    return (
        <Container fluid>
            <div className='page-content'>
                <Row>
                    <Col>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h4 className="card-title mb-0">Gestión de Actividades Económicas</h4>
                                    <Button color="primary" onClick={toggleModal}>
                                        <FaPlus className="me-2" />
                                        Nueva Actividad
                                    </Button>
                                </div>

                                {alert.show && (
                                    <Alert color={alert.type} className="mb-3">
                                        {alert.message}
                                    </Alert>
                                )}

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="position-relative">
                                            <Input
                                                type="text"
                                                placeholder="Buscar actividades..."
                                                value={searchTerm}
                                                onChange={handleSearch}
                                                className="ps-5"
                                            />
                                            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                                        </div>
                                    </Col>
                                </Row>

                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner color="primary" />
                                        <p className="mt-2">Cargando actividades...</p>
                                    </div>
                                ) : (
                                    <>
                                        <Table responsive striped>
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Nombre</th>
                                                    <th>Descripción</th>
                                                    <th>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {params.results.length > 0 ? (
                                                    params.results.map((item) => (
                                                        <tr key={item.id}>
                                                            <td>{item.id}</td>
                                                            <td>{item.nombre}</td>
                                                            <td>{item.descripcion}</td>
                                                            <td>
                                                                <Button
                                                                    color="info"
                                                                    size="sm"
                                                                    className="me-2"
                                                                    onClick={() => handleEdit(item)}
                                                                >
                                                                    <FaEdit />
                                                                </Button>
                                                                <Button
                                                                    color="danger"
                                                                    size="sm"
                                                                    onClick={() => handleDelete(item.id)}
                                                                >
                                                                    <FaTrash />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center">
                                                            No se encontraron actividades económicas
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>

                                        <Pagination
                                            count={params.count}
                                            next={params.next}
                                            previous={params.previous}
                                            limit={30}
                                            filter={true}
                                            baseUrl="/casos/actividades-economicas/"
                                            filterFunction={getFilterData}
                                        />
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>


            {/* Modal para crear/editar */}
            <Modal isOpen={modal} toggle={toggleModal} size="md">
                <ModalHeader toggle={toggleModal}>
                    {editingItem ? 'Editar Actividad' : 'Nueva Actividad'}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="nombre">Nombre *</Label>
                                    <Input
                                        type="text"
                                        id="nombre"
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="descripcion">Descripción</Label>
                                    <Input
                                        type="textarea"
                                        id="descripcion"
                                        rows="3"
                                        value={formData.descripcion}
                                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit">
                            {editingItem ? 'Actualizar' : 'Crear'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </Container>
    );
};

export default ActividadesEconomicas;
